import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AdminSectionCard } from '@/components/admin/AdminSectionCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { clusterBlastRadiusCopy, factToken } from '@/lib/blog/editor-model';
import { buildClusterFormDefaults } from '@/lib/blog/form-defaults';
import { LTR_ISOLATE_ATTRS } from '@/lib/blog/locale-options';
import {
  blogClusterFormSchema,
  type BlogClusterFormInput,
  type BlogClusterFormOutput,
} from '@/lib/schemas/blog-schemas';
import type { Block, FactDefinition } from '@/lib/blog/types';

export interface ClusterInvariantsSubmission {
  facts: FactDefinition[];
  steps: { index: number; minutes: number }[];
}

interface ClusterInvariantsPanelProps {
  facts: FactDefinition[];
  body: Block[];
  localeCount: number;
  saving: boolean;
  onSubmit: (submission: ClusterInvariantsSubmission) => void;
}

/**
 * Fact definitions and hands-on minutes — what every language shares (Q14/Q15).
 *
 * Physically outside the locale form: its own card, its own submit, its own
 * confirm, and no shared dirty state. A cluster control sitting next to the
 * locale Save is how an editor saves every language at once while believing
 * they saved one.
 *
 * AM-18 took the fact *value* out of this panel and left the definition. What a
 * fact is (`id`) and where it was verified (`source`) cannot vary between two
 * copies of the same post without one of them being wrong; how it reads can and
 * must, so it is written next to the prose that carries it. `minutes` stays,
 * because a step takes the same time in every language — it drives the hands-on
 * meter and `HowTo.totalTime` in every copy at once, and unlike a number in
 * prose it is not something a translator renders.
 */
export function ClusterInvariantsPanel({
  facts,
  body,
  localeCount,
  saving,
  onSubmit,
}: ClusterInvariantsPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState<BlogClusterFormOutput | null>(null);

  const defaults = useMemo(() => buildClusterFormDefaults(facts, body), [facts, body]);

  const form = useForm<BlogClusterFormInput, unknown, BlogClusterFormOutput>({
    resolver: zodResolver(blogClusterFormSchema),
    defaultValues: defaults,
  });

  // Read during render so react-hook-form's proxy actually maintains it.
  const factErrors = form.formState.errors.facts;
  const minuteErrors = form.formState.errors.minutes ?? [];

  const submit = form.handleSubmit((values) => {
    setPending(values);
    setConfirming(true);
  });

  /**
   * Takes the values it is confirming as an argument rather than reading the
   * pending state and re-checking it for null: the confirm only exists inside a
   * dialog that is only rendered once there is something pending, so a null
   * check here would be a branch nothing could ever take.
   */
  const confirm = (values: BlogClusterFormOutput) => {
    onSubmit({
      facts: values.facts,
      steps: values.minutes.map((step) => ({ index: step.index, minutes: step.minutes })),
    });
    setConfirming(false);
  };

  return (
    <AdminSectionCard
      title="Shared across every language"
      description="These values are not translated. Editing one here changes every copy of this post."
    >
      <form onSubmit={(event) => void submit(event)} className="space-y-6" noValidate>
        <div className="grid gap-4">
          <h4 className="text-sm font-semibold">Fact definitions</h4>
          <p className="text-sm text-muted-foreground">
            What each fact is, and where the number was verified. How it reads is not here — that
            is language, and it is written next to the prose in each locale&rsquo;s editor.
          </p>
          {defaults.facts.map((fact, index) => {
            const idName: `facts.${number}.id` = `facts.${index}.id`;
            const sourceName: `facts.${number}.source` = `facts.${index}.source`;
            const idError = factErrors?.[index]?.id?.message;
            const sourceError = factErrors?.[index]?.source?.message;
            return (
              <div key={fact.id} className="grid gap-2 rounded-2xl border p-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  {/* Tracks the field as it is typed: the token is what an
                      author pastes into prose, so a stale preview would send
                      them to write a token that no longer resolves. */}
                  <span
                    className="font-mono text-xs text-muted-foreground"
                    data-testid={`fact-token-${fact.id}`}
                    {...LTR_ISOLATE_ATTRS}
                  >
                    {factToken(form.watch(idName))}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`fact-id-${fact.id}`}>Fact id</Label>
                  <Input id={`fact-id-${fact.id}`} {...LTR_ISOLATE_ATTRS} {...form.register(idName)} />
                  {idError !== undefined && (
                    <p className="text-xs text-destructive" role="alert">
                      {idError}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`fact-source-${fact.id}`}>Verified at</Label>
                  <Input id={`fact-source-${fact.id}`} {...LTR_ISOLATE_ATTRS} {...form.register(sourceName)} />
                  {sourceError !== undefined && (
                    <p className="text-xs text-destructive" role="alert">
                      {sourceError}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {facts.length === 0 && (
            <p className="text-sm text-muted-foreground">This post declares no facts.</p>
          )}
        </div>

        <div className="grid gap-4">
          <h4 className="text-sm font-semibold">Hands-on minutes</h4>
          <p className="text-sm text-muted-foreground">
            The declared time a reader spends doing each step. A step takes the same time in every
            language — unlike a number written into prose — so it stays here and stays locked in
            the per-locale editor. It feeds the hands-on meter and the structured data in every
            copy at once.
          </p>
          {defaults.minutes.map((step, index) => {
            const name: `minutes.${number}.minutes` = `minutes.${index}.minutes`;
            const minutesError = minuteErrors[index]?.minutes?.message;
            return (
              <div key={step.index} className="flex items-end gap-3">
                <div className="flex flex-1 flex-col gap-1">
                  <Label htmlFor={`step-minutes-${String(step.index)}`}>{step.title}</Label>
                  <Input
                    id={`step-minutes-${String(step.index)}`}
                    type="number"
                    min={0}
                    className="w-28"
                    {...form.register(name)}
                  />
                  {minutesError !== undefined && (
                    <p className="text-xs text-destructive" role="alert">
                      {minutesError}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {defaults.minutes.length === 0 && (
            <p className="text-sm text-muted-foreground">This post has no hands-on steps.</p>
          )}
        </div>

        <Button type="submit" variant="outline" disabled={saving}>
          {saving ? 'Saving…' : 'Save shared values'}
        </Button>
      </form>

      {pending !== null && (
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent data-testid="cluster-confirm">
            <DialogHeader>
              <DialogTitle>Change values in every language?</DialogTitle>
              <DialogDescription>{clusterBlastRadiusCopy(localeCount)}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirming(false); }}>
                Cancel
              </Button>
              <Button onClick={() => { confirm(pending); }}>Save shared values</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminSectionCard>
  );
}
